import type React from 'react';
import type { PlanDefinition, PlanKey } from '@hospasense/contracts';
import { Button, Modal, Input, Select, SearchableSelect } from '../components/ui/index.js';

interface CreateContractModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  organisations: Array<{ id: string; displayName: string }>;
  plans: PlanDefinition[];
  selectedOrgId: string;
  setSelectedOrgId: (val: string) => void;
  selectedPlanKey: PlanKey;
  setSelectedPlanKey: (val: PlanKey) => void;
  acvInr: number;
  setAcvInr: (val: number) => void;
  stateCode: string;
  setStateCode: (val: string) => void;
  gstin: string;
  setGstin: (val: string) => void;
}

export function CreateContractModal({
  open,
  onClose,
  onSubmit,
  organisations,
  plans,
  selectedOrgId,
  setSelectedOrgId,
  selectedPlanKey,
  setSelectedPlanKey,
  acvInr,
  setAcvInr,
  stateCode,
  setStateCode,
  gstin,
  setGstin,
}: CreateContractModalProps) {
  if (!open) return null;
  return (
    <Modal open={open} title="Create Commercial Contract" onClose={onClose}>
      <form onSubmit={onSubmit} className="space-y-3">
        <SearchableSelect
          label="Organisation"
          required
          placeholder="Search and select client organisation…"
          value={selectedOrgId}
          onChange={(val) => setSelectedOrgId(val)}
          options={organisations.map((org) => ({
            value: org.id,
            label: org.displayName,
          }))}
        />

        <Select
          label="Plan"
          value={selectedPlanKey}
          onChange={(e) => {
            const k = e.target.value as PlanKey;
            setSelectedPlanKey(k);
            const p = plans.find((pl) => pl.key === k);
            if (p) setAcvInr(p.basePriceAnnualInr);
          }}
        >
          {plans.map((p) => (
            <option key={p.key} value={p.key}>
              {p.name} (₹{(p.basePriceAnnualInr / 100000).toFixed(1)}L)
            </option>
          ))}
        </Select>

        <div className="grid grid-cols-2 gap-2">
          <Input
            label="Annual Value (INR)"
            type="number"
            value={String(acvInr)}
            onChange={(e) => setAcvInr(Number(e.target.value))}
            required
          />
          <Input
            label="State Code"
            value={stateCode}
            onChange={(e) => setStateCode(e.target.value.toUpperCase())}
            required
          />
        </div>

        <Input
          label="Customer GSTIN"
          value={gstin}
          onChange={(e) => setGstin(e.target.value)}
          required
        />

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit">
            Create Contract
          </Button>
        </div>
      </form>
    </Modal>
  );
}

interface AddAmendmentModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  amendSummary: string;
  setAmendSummary: (val: string) => void;
  amendDelta: number;
  setAmendDelta: (val: number) => void;
}

export function AddAmendmentModal({
  open,
  onClose,
  onSubmit,
  amendSummary,
  setAmendSummary,
  amendDelta,
  setAmendDelta,
}: AddAmendmentModalProps) {
  if (!open) return null;
  return (
    <Modal open={open} title="Add Contract Amendment" onClose={onClose}>
      <form onSubmit={onSubmit} className="space-y-3">
        <Input
          label="Summary"
          value={amendSummary}
          onChange={(e) => setAmendSummary(e.target.value)}
          placeholder="e.g. Added 20 ICU beds"
          required
        />
        <Input
          label="Financial Delta (INR)"
          type="number"
          value={String(amendDelta)}
          onChange={(e) => setAmendDelta(Number(e.target.value))}
          required
        />
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit">
            Save Amendment
          </Button>
        </div>
      </form>
    </Modal>
  );
}
